import { sanitize } from 'isomorphic-dompurify';

import { ParserManager } from '../parser';
import * as Command from '../parser/commands';
import { convertToConsoleHexString } from '../parser/util';
import { AstNode, CommandByteSequence, PixelData, RenderResult, SvgData } from '../types';
import { getPrefixTransformStyleString } from './css';
import * as Html from './html';
import { PrinterContext } from './printer';
import * as Text from './text';
import { walkAST } from './util';

class RendererManager {
  #renderAs: RenderResult;
  #printerContext: PrinterContext;
  #resultStrings: string[];

  constructor(renderAs: RenderResult, printerContext: PrinterContext) {
    this.#renderAs = renderAs;
    this.#printerContext = printerContext;
    this.#resultStrings = [];
  }

  get resultStrings() {
    return this.#resultStrings;
  }

  pushImageBuffer() {
    this.#resultStrings.push(this.#printerContext.getImageBuffer());
  }

  renderTwoDimensionalCode(type: 'barcode' | 'pdf417' | 'qrcode') {
    this.#resultStrings.push(
      (() => {
        switch (this.#renderAs) {
          case 'html':
            return Html.twoDimensionalCodeImage(type, this.#printerContext);
        }
      })()
    );
  }

  renderImage(extractedImageData: PixelData[][], additionalStyles: Record<string, string> = {}) {
    switch (this.#renderAs) {
      case 'html':
        return Html.wrapInDiv(Html.svgImage(extractedImageData, this.#printerContext), {
          display: 'flex',
          ...additionalStyles,
        });
    }
  }

  renderChunkImage(extractedImageData: PixelData[][]) {
    switch (this.#renderAs) {
      case 'html':
        return Html.svgChunkImage(extractedImageData);
    }
  }

  compileChunkImage(chunkImages: SvgData[]) {
    this.#resultStrings.push(
      (() => {
        switch (this.#renderAs) {
          case 'html':
            return Html.wrapInDiv(Html.compileSvgChunkImage(chunkImages, this.#printerContext), {
              display: 'flex',
              height: `${Number(chunkImages[0].styles.height) * (chunkImages.length - 1)}px`,
            });
        }
      })()
    );
  }

  print(extractedTextData: string, possibleMaximumWidth = 32) {
    this.#resultStrings.push(
      (() => {
        switch (this.#renderAs) {
          case 'html':
            return Html.text(extractedTextData, this.#printerContext);

          case 'text':
            return Text.text(extractedTextData, this.#printerContext, possibleMaximumWidth);
        }
      })()
    );
  }

  printAndLineFeed(extractedTextData: string, nLines = 1, possibleMaximumWidth = 32) {
    this.#resultStrings.push(
      (() => {
        switch (this.#renderAs) {
          case 'html':
            return Html.text(extractedTextData, this.#printerContext).concat(this.#lineFeedNtimes(nLines));

          case 'text':
            return Text.text(extractedTextData, this.#printerContext, possibleMaximumWidth).concat(this.#lineFeedNtimes(nLines));
        }
      })()
    );
  }

  #lineFeedNtimes = (nLines: number) => {
    switch (this.#renderAs) {
      case 'html': {
        let lineBreaks = '';
        for (let i = 0; i < nLines; i++) {
          lineBreaks += Html.lineBreak();
        }
        return lineBreaks;
      }

      case 'text': {
        let lineBreaks = '';
        for (let i = 0; i < nLines; i++) {
          lineBreaks += '\n';
        }
        return lineBreaks;
      }
    }
  };
}

const findBeforeResetCommand = (commandToSearch: CommandByteSequence, ast: AstNode[]) => {
  for (const node of ast) {
    if (node.command === `${Command.ESC}\x40`) return false;
    if (node.command === commandToSearch) return true;
  }

  return false;
};

const renderAsHTML = (ast: AstNode[], printerContext: PrinterContext) => {
  const rendererManager = new RendererManager('html', printerContext);

  for (const [index, node, textData, commandHandled] of walkAST(ast, printerContext)) {
    switch (node.command) {
      case Command.selectBitImageMode:
        // Add up all image as each chunk (for each column) and then compile
        // them together as one in the last `ESC *` command
        if (findBeforeResetCommand(Command.selectBitImageMode, ast.slice(index + 1))) {
          printerContext.setColumnImageBuffer([...printerContext.getImageColumnBuffer(), rendererManager.renderChunkImage(node.extractedData.image)]);
        } else {
          rendererManager.compileChunkImage([...printerContext.getImageColumnBuffer(), rendererManager.renderChunkImage(node.extractedData.image)]);
        }
        break;

      case Command.specifyGraphicsData:
      case Command.specifyGraphicsDataAlternative: {
        const [, , , fn] = node.parsedArgs as number[];

        switch (fn) {
          case 2:
          case 50:
            if (printerContext.getImageBuffer() !== '') {
              rendererManager.pushImageBuffer();
            } else {
              console.log(`[Warning]: Image buffer is empty, make sure you run the function 112 of ${convertToConsoleHexString(node.command)} command first`);
            }
            break;

          case 112:
            printerContext.setImageBuffer(rendererManager.renderImage(node.extractedData.image));
            break;
        }
        break;
      }

      case Command.printRasterBitImage:
        printerContext.setImageBuffer(
          rendererManager.renderImage(node.extractedData.image, {
            'transform-origin': 'left',
            ...(getPrefixTransformStyleString(`scale(0.75)`, 'object') as object),
          })
        );
        rendererManager.pushImageBuffer();
        break;

      case Command.printBarcode:
        rendererManager.renderTwoDimensionalCode('barcode');
        printerContext.setCodeBuffer([]);
        break;

      case Command.print2dCodes: {
        const [, , cn, fn] = (node.parsedArgs as number[]).slice(0, 4);

        if (fn === 81) {
          const type: 'qrcode' | 'pdf417' = (() => {
            if (cn === 48) {
              return 'pdf417';
            } else if (cn === 49) {
              return 'qrcode';
            }

            return null;
          })();

          if (type !== null) {
            rendererManager.renderTwoDimensionalCode(type);
            printerContext.setCodeBuffer([]);
          } else {
            console.log(`[Warning]: cn value of "${cn}" for function 81 of ${convertToConsoleHexString(node.command)} command is currently not supported`);
          }
        }
        break;
      }

      case Command.printAndFeedXLine: {
        if (ast[index - 1]?.command === Command.selectBitImageMode) {
          // If previous command is `ESC *` then ignore the linefeed
          break;
        }

        const [nLines] = node.parsedArgs as number[];
        rendererManager.printAndLineFeed(textData, nLines);
        break;
      }

      case `${Command.LF}`:
      case `${Command.CR}`:
        if (ast[index - 1]?.command === Command.selectBitImageMode) {
          // If previous command is `ESC *` then ignore the linefeed
          break;
        }

        rendererManager.printAndLineFeed(textData);
        break;

      default:
        if (node.command === '' && textData.length > 0) {
          rendererManager.print(textData);
        } else if (!commandHandled) {
          console.log(`[Warning]: [${convertToConsoleHexString(node.command)}] command have no renderer function`);
        }
        break;
    }
  }

  return rendererManager.resultStrings;
};

const renderAsRawText = (ast: AstNode[], printerContext: PrinterContext) => {
  const resultStrings: string[] = [];

  for (const [, node] of walkAST(ast, printerContext)) {
    if (!node?.extractedData?.text && node.command !== Command.LF) {
      continue;
    }

    let currentTextBytes: {
      byte: string;
      position: number;
      isClean: boolean;
    }[] = [];

    if (node.command === Command.LF && !node?.extractedData?.text) {
      // If printing without text (e.g. when we just want to break current line)
      // then just assume it has one space character
      currentTextBytes = [
        {
          byte: '20',
          position: 0,
          isClean: true,
        },
      ];
    } else {
      currentTextBytes = node.extractedData.text.map((x: string, index: number) => {
        return {
          byte: x.charCodeAt(0).toString(16).padStart(2, '0'),
          position: index,
          isClean: true,
        };
      });
    }

    /**
     * Cleanup garbage bytes
     *
     * Garbage bytes in this case are those that couldnt directly rendered in the browser such
     * as those inside of a one byte encoding system (codepages) and other stuff the could be
     * something inside of a command
     */
    for (const [index, char] of currentTextBytes.entries()) {
      try {
        // Try decoding current byte
        const decodedByte = decodeURIComponent(`%${char.byte}`);

        // decodedByte below 0x20 are considered garbage bytes
        if (/^[\x00-\x19]*$/.test(decodedByte)) {
          currentTextBytes[index].isClean = false;
        }
      } catch {
        currentTextBytes[index].isClean = false;
      }
    }

    /**
     * Check if there are unicodes inside garbage bytes
     * and treat them as clean bytes instead
     */
    let possiblyUnicodeBytes = [];
    for (const [index, char] of currentTextBytes.entries()) {
      if (char.isClean) possiblyUnicodeBytes = [];

      if (!char.isClean) {
        possiblyUnicodeBytes.push(char.byte);
      }

      if (possiblyUnicodeBytes.length > 0) {
        try {
          // Try decoding current byte
          decodeURIComponent('%'.concat(possiblyUnicodeBytes.join('%')));

          // Set all unicode bytes as clean if succeed
          for (let pos = index - (possiblyUnicodeBytes.length - 1); pos < index + 1; pos++) {
            currentTextBytes[pos].isClean = true;
          }
        } catch {
          continue;
        }
      }
    }

    // Merge all clean bytes
    const cleanBytes = '%'.concat(
      currentTextBytes
        .filter((char) => char.isClean)
        .map((char) => char.byte)
        .join('%')
    );

    resultStrings.push(decodeURIComponent(cleanBytes));
  }

  return resultStrings;
};

const renderAsText = (ast: AstNode[], printerContext: PrinterContext, possibleMaxWidth: number) => {
  const rendererManager = new RendererManager('text', printerContext);

  for (const [index, node, textData, commandHandled] of walkAST(ast, printerContext)) {
    switch (node.command) {
      case Command.printAndFeedXLine: {
        if (ast[index - 1]?.command === Command.selectBitImageMode) {
          // If previous command is `ESC *` then ignore the linefeed
          break;
        }

        const [nLines] = node.parsedArgs as number[];
        rendererManager.printAndLineFeed(textData, nLines, possibleMaxWidth);
        break;
      }

      case `${Command.LF}`:
      case `${Command.CR}`:
        if (ast[index - 1]?.command === Command.selectBitImageMode) {
          // If previous command is `ESC *` then ignore the linefeed
          break;
        }

        rendererManager.printAndLineFeed(textData, 1, possibleMaxWidth);
        break;

      default:
        if (node.command === '' && textData.length > 0) {
          rendererManager.print(textData, possibleMaxWidth);
        } else if (!commandHandled) {
          console.log(`[Warning]: [${convertToConsoleHexString(node.command)}] command have no renderer function`);
        }
        break;
    }
  }

  return rendererManager.resultStrings;
};

export const render = (binaryReceiptData: string[] | string, renderAs: RenderResult = 'html') => {
  const printerContext = new PrinterContext();
  const parseResult = new ParserManager().parseRawData(binaryReceiptData);
  const ast = parseResult.ast;
  const possibleMaxWidth = parseResult.possibleMaxWidth;

  const result = (() => {
    switch (renderAs) {
      case 'html':
        return sanitize(
          `
          <div style="
            font-family: monospace;
            display: inline-block;
            white-space: pre;
            margin-left: auto;
            margin-right: auto;
            max-width: 100%;
          ">${renderAsHTML(ast, printerContext).join('')}</div>`.trim()
        );

      case 'rawtext':
        return sanitize(
          `
          <div style="
            font-family: monospace;
            display: inline-block;
            white-space: pre;
            margin-left: auto;
            margin-right: auto;
            max-width: 100%;
          "><span>${renderAsRawText(ast, printerContext).join('\n')}</span></div>`.trim()
        );

      case 'text':
        return renderAsText(ast, printerContext, possibleMaxWidth).join('').trim();

      case 'png':
        throw new Error('PNG renderer is not implemented yet');
    }
  })();

  return {
    result,
    ast,
  };
};
